import React, { useEffect } from "react";
import moment from "moment";
import { useAppContext } from "../../AppContext";
import MPHomeLoginWithPhoneNumber from "./MPHomeLoginWithPhoneNumber";
import { useTranslation } from "react-i18next";

const C = {
  primary: "#1A1A2E",
  primaryHover: "#16213E",
  accent: "#C9A84C",
  accentLight: "#FDF6E3",
  accentMuted: "rgba(201,168,76,0.12)",
  success: "#10b981",
  border: "#e5e7eb",
  textMuted: "#6b7280",
  textLight: "#9ca3af",
  disabled: "#e5e7eb",
  disabledText: "#9ca3af",
};

export default function MPHomeBookApp() {
  const {
    OpenDrawer,
    setOpenDrawer,
    ObjectData,
    setObjectData,
    AllApps,
    Loading,
    ChoosedDate,
    setChoosedDate,
    ChoosedApps,
    setChoosedApps,
    AllData,
    SelectedServices,
    setSelectedServices,
    User,
    setUser,
    OtherBarbers,
    setStep,
    step,
    SelectedBarber,
    setSelectedBarber,
  } = useAppContext();
  const { t, i18n } = useTranslation();

  const shouldShowBarberStep = OtherBarbers && OtherBarbers.length > 1;

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && !User) {
          setUser(parsedUser);
          if (shouldShowBarberStep) setStep(4);
          else setStep(5);
        }
      } catch (err) {
        localStorage.removeItem("user");
      }
    }
  }, []);

  const getCurrencySymbol = (countryCode) => {
    const currencyMap = {
      "+972": "₪",
      "+46": "kr",
      "+1": "$",
      "+44": "£",
      "+49": "€",
    };
    return currencyMap[countryCode] || "₪";
  };

  const currencySymbol = getCurrencySymbol(User?.countryNumber);

  useEffect(() => {
    if (!User) {
      setStep(1);
      setSelectedServices([]);
      setChoosedDate(null);
      setChoosedApps([]);
      setSelectedBarber(null);
    } else {
      if (step >= 1 && step <= 3) {
        if (shouldShowBarberStep) setStep(4);
        else setStep(5);
      }
    }
  }, [User]);

  const toggleService = (item) => {
    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.Service === item.service);
      if (exists) return prev.filter((s) => s.Service !== item.service);
      let durationValue = 30;
      if (item.duration) {
        const parsed = parseInt(
          item.duration.toString().match(/\d+/)?.[0] || "30",
        );
        durationValue = parsed || 30;
      }
      return [
        ...prev,
        { Service: item.service, Price: item.price, Duration: durationValue },
      ];
    });
  };

  const days = Array.from({ length: 30 }, (_, i) => {
    const dayMoment = moment().add(i, "days");
    const dayIndex = dayMoment.day();
    const monthIndex = dayMoment.month();
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return {
      date: dayMoment.format("DD-MM-YYYY"),
      day: t(dayNames[dayIndex]),
      fullDay: t(dayNames[dayIndex]),
      dayNum: dayMoment.format("DD"),
      monthName: t(monthNames[monthIndex]),
    };
  });

  const totalPrice = SelectedServices.reduce(
    (sum, service) => sum + parseFloat(service.Price || 0),
    0,
  );
  const totalDuration = SelectedServices.reduce(
    (sum, service) => sum + (service.Duration || 0),
    0,
  );

  const getAvailableStartingSlots = (appointments, requiredDuration) => {
    if (requiredDuration === 0) {
      return appointments
        .map((app, index) => ({ app, index }))
        .filter(({ app }) => app.isTaken === 0)
        .map(({ index }) => index);
    }
    const validStartIndexes = [];
    const consumedIndexes = new Set();
    for (let i = 0; i < appointments.length; i++) {
      const app = appointments[i];
      if (consumedIndexes.has(i) || app.isTaken === 1 || app.isTaken === 4)
        continue;
      let accumulatedTime = 0;
      let currentIndex = i;
      const slotsNeeded = [];
      while (
        currentIndex < appointments.length &&
        accumulatedTime < requiredDuration
      ) {
        const currentApp = appointments[currentIndex];
        if (currentApp.isTaken === 1 || currentApp.isTaken === 4) break;
        if (currentIndex > i) {
          const prevApp = appointments[currentIndex - 1];
          if (prevApp.timeEnd !== currentApp.timeStart) break;
        }
        slotsNeeded.push(currentIndex);
        accumulatedTime += currentApp.timeLength;
        currentIndex++;
      }
      if (accumulatedTime >= requiredDuration) {
        validStartIndexes.push(i);
        for (let j = 1; j < slotsNeeded.length; j++)
          consumedIndexes.add(slotsNeeded[j]);
      }
    }
    return validStartIndexes;
  };

  const handleNextToServices = () => {
    if (SelectedBarber || !shouldShowBarberStep) setStep(5);
  };

  const handleNextToDateTime = () => {
    if (SelectedServices.length > 0) {
      const today = moment().format("DD-MM-YYYY");
      setChoosedDate(today);
      let todaysAppointments = [];
      if (SelectedBarber) {
        const barberData = AllData.allBarbersWithData?.find(
          (b) => b.barber.phoneNumber === SelectedBarber.phoneNumber,
        );
        todaysAppointments =
          barberData?.appointments.filter(
            (app) => app.date === today && app.isTaken === 0,
          ) || [];
      } else {
        todaysAppointments = AllApps.filter((app) => app.date === today);
      }
      setChoosedApps(todaysAppointments);
      setStep(6);
    }
  };

  const handleBackToBarber = () => {
    if (shouldShowBarberStep) setStep(4);
  };

  const handleBackToServices = () => setStep(5);

  const handleLoginSuccess = (userData) => {
    if (userData) {
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
      if (shouldShowBarberStep) setStep(4);
      else setStep(5);
    }
  };

  if (!Loading) return <div>{t("Loading")}...</div>;

  // ── Shared sub-components ────────────────────────────────────────────────

  const SectionHeader = ({ title, subtitle }) => (
    <div style={{ marginBottom: "20px", textAlign: "center" }}>
      <h3
        style={{
          fontSize: "22px",
          fontWeight: "700",
          color: C.primary,
          marginBottom: "6px",
        }}
      >
        {title}
      </h3>
      {subtitle && (
        <p style={{ fontSize: "14px", color: C.textMuted }}>{subtitle}</p>
      )}
    </div>
  );

  const PrimaryBtn = ({ onClick, disabled, label }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "16px",
        fontSize: "16px",
        fontWeight: "700",
        border: "none",
        borderRadius: "12px",
        backgroundColor: disabled ? C.disabled : C.primary,
        color: disabled ? C.disabledText : C.accent,
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: disabled ? "none" : "0 4px 16px rgba(26,26,46,0.35)",
        transition: "all 0.3s ease",
        outline: "none",
        letterSpacing: "0.3px",
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 6px 20px rgba(26,26,46,0.45)";
          e.currentTarget.style.backgroundColor = C.primaryHover;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(26,26,46,0.35)";
          e.currentTarget.style.backgroundColor = C.primary;
        }
      }}
    >
      {label}
    </button>
  );

  const BackBtn = ({ onClick, label }) => (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        padding: "14px",
        fontSize: "15px",
        fontWeight: "600",
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
        color: C.textMuted,
        cursor: "pointer",
        transition: "all 0.3s ease",
        outline: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = C.accent;
        e.currentTarget.style.color = C.accent;
        e.currentTarget.style.backgroundColor = C.accentMuted;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.color = C.textMuted;
        e.currentTarget.style.backgroundColor = "#fff";
      }}
    >
      {label}
    </button>
  );

  // ── Progress stepper (post-login, steps 4-6) ─────────────────────────────

  const ProgressStepper = () => {
    const steps = [];
    if (shouldShowBarberStep) steps.push({ label: t("Barber"), target: 4 });
    steps.push({ label: t("Services"), target: 5 });
    steps.push({ label: t("Book"), target: 6 });

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        {steps.map((s, i) => {
          const active = step === s.target;
          const done = step > s.target;
          const num = i + 1;
          return (
            <React.Fragment key={s.target}>
              {i > 0 && (
                <div
                  style={{
                    width: "30px",
                    height: "2px",
                    backgroundColor: done || active ? C.accent : C.border,
                    transition: "background-color 0.3s ease",
                  }}
                />
              )}
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: done
                      ? C.success
                      : active
                        ? C.accent
                        : C.border,
                    color: done || active ? C.primary : C.textLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "12px",
                    transition: "all 0.3s ease",
                  }}
                >
                  {done ? "✓" : num}
                </div>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: active ? "700" : "500",
                    color: done ? C.success : active ? C.accent : C.textLight,
                    transition: "color 0.3s ease",
                  }}
                >
                  {s.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // ── Selected barber banner ────────────────────────────────────────────────

  const BarberBanner = ({ showChange = false }) =>
    SelectedBarber ? (
      <div
        style={{
          marginBottom: "20px",
          padding: "14px 16px",
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryHover} 100%)`,
          borderRadius: "14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: `1px solid rgba(201,168,76,0.25)`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              backgroundColor: C.accent,
              color: C.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "700",
              fontSize: "16px",
            }}
          >
            {SelectedBarber.name?.charAt(0) || "B"}
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#fff" }}>
              {SelectedBarber.name || t("Barber")}
            </div>
            <div style={{ fontSize: "12px", color: C.accent, opacity: 0.9 }}>
              {t("YourBarber")}
            </div>
          </div>
        </div>
        {showChange && shouldShowBarberStep && (
          <button
            onClick={handleBackToBarber}
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: C.accent,
              backgroundColor: "rgba(201,168,76,0.15)",
              border: `1px solid rgba(201,168,76,0.4)`,
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: "8px",
              outline: "none",
              transition: "all 0.2s ease",
            }}
          >
            {t("Change")}
          </button>
        )}
      </div>
    ) : null;

  // ── Selection card (barbers & services) ──────────────────────────────────

  const SelectionCard = ({ isSelected, onClick, children }) => (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "18px",
        borderRadius: "14px",
        backgroundColor: isSelected ? C.primary : "#fff",
        color: isSelected ? "#fff" : C.primary,
        border: `2px solid ${isSelected ? C.accent : C.border}`,
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: isSelected
          ? "0 4px 16px rgba(26,26,46,0.35)"
          : "0 2px 6px rgba(0,0,0,0.06)",
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = C.accent;
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = `0 4px 12px rgba(201,168,76,0.2)`;
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.06)";
        }
      }}
    >
      {children}
    </div>
  );

  const RadioDot = ({ isSelected }) => (
    <div
      style={{
        width: "26px",
        height: "26px",
        borderRadius: "50%",
        border: `2px solid ${isSelected ? "#fff" : C.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isSelected ? "#fff" : "transparent",
        flexShrink: 0,
      }}
    >
      {isSelected && (
        <div
          style={{
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: C.accent,
          }}
        />
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        paddingBottom: "40px",
      }}
    >
      {/* Title */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 20,
        }}
      >
        <span
          style={{
            fontSize: 18,
            fontWeight: "700",
            color: C.primary,
            letterSpacing: "0.3px",
          }}
        >
          {t("BookAppointment")}
        </span>
      </div>

      {/* Progress (post-login) */}
      {step > 3 && <ProgressStepper />}

      {/* STEPS 1–3: Authentication */}
      {step >= 1 && step <= 3 && (
        <div style={{ maxWidth: "500px", margin: "0 auto", width: "100%" }}>
          <MPHomeLoginWithPhoneNumber onLoginSuccess={handleLoginSuccess} />
        </div>
      )}

      {/* STEP 4: Choose Barber */}
      {step === 4 && shouldShowBarberStep && (
        <div style={{ paddingBottom: "24px" }}>
          <SectionHeader
            title={t("ChooseYourBarber")}
            subtitle={t("SelectBarberForAppointment")}
          />

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {OtherBarbers.map((barber, index) => {
              const isSelected =
                SelectedBarber?.phoneNumber === barber.phoneNumber;
              return (
                <SelectionCard
                  key={index}
                  isSelected={isSelected}
                  onClick={() => setSelectedBarber(barber)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        backgroundColor: isSelected ? C.accent : C.accentMuted,
                        color: isSelected ? C.primary : C.accent,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "700",
                        fontSize: "18px",
                        flexShrink: 0,
                        border: isSelected
                          ? "none"
                          : `1.5px solid rgba(201,168,76,0.3)`,
                      }}
                    >
                      {barber.name?.charAt(0) || "B"}
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "4px",
                        }}
                      >
                        {barber.name || t("Barber")}
                      </div>
                      <div style={{ fontSize: "13px", opacity: 0.75 }}>
                        {barber.specialty || t("ProfessionalBarber")}
                      </div>
                    </div>
                  </div>
                  <RadioDot isSelected={isSelected} />
                </SelectionCard>
              );
            })}
          </div>

          <div
            style={{
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: `2px solid #f3f4f6`,
            }}
          >
            <PrimaryBtn
              onClick={handleNextToServices}
              disabled={!SelectedBarber}
              label={t("NextSelectServices")}
            />
          </div>
        </div>
      )}

      {/* STEP 5: Select Services */}
      {step === 5 && (
        <div style={{ paddingBottom: "24px" }}>
          <BarberBanner showChange />

          <SectionHeader
            title={t("SelectYourServices")}
            subtitle={t("ChooseOneOrMoreServices")}
          />

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {(() => {
              let servicesToShow = [];
              if (SelectedBarber) {
                const barberData = AllData.allBarbersWithData?.find(
                  (b) => b.barber.phoneNumber === SelectedBarber.phoneNumber,
                );
                servicesToShow = barberData?.services || [];
              } else {
                servicesToShow = AllData.barberServices || [];
              }

              return servicesToShow.length > 0 ? (
                servicesToShow.map((item, index) => {
                  const isSelected = SelectedServices.some(
                    (s) => s.Service === item.service,
                  );
                  return (
                    <SelectionCard
                      key={index}
                      isSelected={isSelected}
                      onClick={() => toggleService(item)}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            marginBottom: "4px",
                          }}
                        >
                          {item.service}
                        </div>
                        <div style={{ fontSize: "13px", opacity: 0.75 }}>
                          {item.duration || "30"} {t("Minutes")}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "14px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "700",
                            fontSize: "19px",
                            color: isSelected ? C.accent : C.primary,
                          }}
                        >
                          {item.price} {currencySymbol}
                        </div>
                        <RadioDot isSelected={isSelected} />
                      </div>
                    </SelectionCard>
                  );
                })
              ) : (
                <div
                  style={{
                    padding: "40px 20px",
                    textAlign: "center",
                    backgroundColor: "#fef2f2",
                    borderRadius: "16px",
                    border: "2px solid #fca5a5",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                    ⚠️
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: "#991b1b",
                      fontSize: "16px",
                    }}
                  >
                    {t("NoServicesAvailable")}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#dc2626",
                      marginTop: "6px",
                    }}
                  >
                    {t("ThisBarberHasNoServices")}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Total summary */}
          {SelectedServices.length > 0 && (
            <div
              style={{
                marginTop: "20px",
                padding: "18px",
                borderRadius: "14px",
                backgroundColor: C.accentLight,
                border: `1px solid rgba(201,168,76,0.25)`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <span
                  style={{
                    fontWeight: "600",
                    fontSize: "15px",
                    color: C.textMuted,
                  }}
                >
                  {t("TotalDuration")}
                </span>
                <span
                  style={{
                    fontWeight: "700",
                    fontSize: "17px",
                    color: C.primary,
                  }}
                >
                  {totalDuration} {t("Minutes")}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontWeight: "600",
                    fontSize: "15px",
                    color: C.textMuted,
                  }}
                >
                  {t("TotalPrice")}
                </span>
                <span
                  style={{
                    fontWeight: "700",
                    fontSize: "22px",
                    color: C.accent,
                  }}
                >
                  {totalPrice.toFixed(2)} {currencySymbol}
                </span>
              </div>
            </div>
          )}

          <div
            style={{
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: `2px solid #f3f4f6`,
            }}
          >
            <PrimaryBtn
              onClick={handleNextToDateTime}
              disabled={SelectedServices.length === 0}
              label={t("NextSelectDateTime")}
            />
          </div>
        </div>
      )}

      {/* STEP 6: Select Date & Time */}
      {step === 6 && (
        <div style={{ paddingBottom: "24px" }}>
          <BarberBanner />

          {/* Date picker */}
          <div style={{ marginBottom: "28px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: C.primary,
                marginBottom: "16px",
              }}
            >
              {t("SelectDate")}
            </h3>
            <div
              style={{
                display: "flex",
                overflowX: "auto",
                gap: "10px",
                padding: "4px 2px",
                scrollbarWidth: "thin",
                scrollbarColor: `${C.accent} #f1f5f9`,
              }}
            >
              {days.map((item, index) => {
                const isSelected = item.date === ChoosedDate;
                const isToday = item.date === moment().format("DD-MM-YYYY");
                return (
                  <div
                    key={index}
                    onClick={() => {
                      setChoosedDate(item.date);
                      let todaysAppointments = [];
                      if (SelectedBarber) {
                        const barberData = AllData.allBarbersWithData?.find(
                          (b) =>
                            b.barber.phoneNumber === SelectedBarber.phoneNumber,
                        );
                        todaysAppointments =
                          barberData?.appointments.filter(
                            (app) =>
                              app.date === item.date && app.isTaken === 0,
                          ) || [];
                      } else {
                        todaysAppointments = AllApps.filter(
                          (app) => app.date === item.date,
                        );
                      }
                      setChoosedApps(todaysAppointments);
                    }}
                    style={{
                      minWidth: "86px",
                      padding: "14px 10px",
                      borderRadius: "16px",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: isSelected ? C.primary : "#fff",
                      color: isSelected ? "#fff" : C.primary,
                      border: isSelected
                        ? `2px solid ${C.accent}`
                        : isToday
                          ? `2px solid ${C.accent}`
                          : `2px solid ${C.border}`,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: isSelected
                        ? "0 6px 20px rgba(26,26,46,0.3)"
                        : "0 2px 8px rgba(0,0,0,0.04)",
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform =
                          "translateY(-4px) scale(1.02)";
                        e.currentTarget.style.boxShadow = `0 6px 20px rgba(201,168,76,0.2)`;
                        e.currentTarget.style.borderColor = C.accent;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform =
                          "translateY(0) scale(1)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.04)";
                        e.currentTarget.style.borderColor = isToday
                          ? C.accent
                          : C.border;
                      }
                    }}
                  >
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "600",
                        marginBottom: "4px",
                        opacity: isSelected ? 0.85 : 0.55,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: isSelected ? C.accent : "inherit",
                      }}
                    >
                      {item.monthName}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        marginBottom: "8px",
                        textTransform: "capitalize",
                      }}
                    >
                      {item.day}
                    </div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        lineHeight: "1",
                        color: isSelected ? C.accent : "inherit",
                      }}
                    >
                      {item.dayNum}
                    </div>
                    {isToday && !isSelected && (
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "9px",
                          fontWeight: "700",
                          color: C.accent,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Today
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Time slots */}
          <div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: C.primary,
                marginBottom: "16px",
              }}
            >
              {t("AvailableTimes")}
            </h3>

            {(() => {
              const validIndexes = getAvailableStartingSlots(
                ChoosedApps,
                totalDuration,
              );

              return validIndexes.length === 0 ? (
                <div
                  style={{
                    padding: "40px 20px",
                    textAlign: "center",
                    backgroundColor: "#fef2f2",
                    borderRadius: "16px",
                    border: "2px solid #fca5a5",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                    📅
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: "#991b1b",
                      fontSize: "16px",
                    }}
                  >
                    {t("NoAvailableAppointments")}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#dc2626",
                      marginTop: "6px",
                    }}
                  >
                    {totalDuration > 0
                      ? t("NoConsecutiveSlotsFound", {
                          duration: totalDuration,
                        })
                      : t("PleaseSelectAnotherDate")}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    height: "400px",
                    gap: "10px",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: "10px",
                  }}
                >
                  {validIndexes.map((index) => {
                    const item = ChoosedApps[index];
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          const appointmentData = {
                            ...item,
                            selectedBarber: SelectedBarber,
                          };
                          setObjectData(appointmentData);
                          setOpenDrawer(!OpenDrawer);
                        }}
                        style={{
                          width: "100px",
                          height: "calc(25% - 8px)",
                          padding: "14px 10px",
                          borderRadius: "14px",
                          textAlign: "center",
                          backgroundColor: "#fff",
                          border: `1.5px solid ${C.border}`,
                          cursor: "pointer",
                          transition: "all 0.25s ease",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                          flexShrink: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = C.primary;
                          e.currentTarget.style.borderColor = C.accent;
                          e.currentTarget.style.color = C.accent;
                          e.currentTarget.style.transform = "scale(1.06)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(26,26,46,0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff";
                          e.currentTarget.style.borderColor = C.border;
                          e.currentTarget.style.color = C.primary;
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 6px rgba(0,0,0,0.06)";
                        }}
                      >
                        <div style={{ fontSize: "17px", fontWeight: "700" }}>
                          {item.timeStart}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Back button */}
          <div
            style={{
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: "2px solid #f3f4f6",
            }}
          >
            <BackBtn
              onClick={handleBackToServices}
              label={t("BackToServices")}
            />
          </div>
        </div>
      )}
    </div>
  );
}
